/**
 * Hostname → backend API origin mapping for every River deployment target.
 *
 * Single source of truth for "which backend does this browser talk to". Kept
 * as a side-effect-free pure function (no `window` access at module scope) so
 * modules that must stay importable outside a browser can use it, unlike
 * `api/config.ts`, which resolves its exports at import time.
 *
 * Each rule matches the apex host AND any subdomain of it, so `www.` (and any
 * other subdomain reaching the SPA ahead of an edge www-strip) resolves to the
 * right backend instead of falling through to the local-development origin.
 */

const API_ORIGIN_RULES: ReadonlyArray<{ host: string; apiOrigin: string }> = [
  { host: 'riversystem.ai', apiOrigin: 'https://app.riversystem.ai' },
  // Both production domains are served by the same backend ALB; each is reached
  // through its own `api.` subdomain to bypass CloudFront's 60s timeout limit.
  { host: 'rivereditor.com', apiOrigin: 'https://api.rivereditor.com' },
  { host: 'meetriver.com', apiOrigin: 'https://api.meetriver.com' },
];

export const LOCAL_API_ORIGIN = 'http://localhost:5001';

/** API origin for a known River host, or `undefined` for anything else. */
export function getApiOriginForHostname(hostname: string): string | undefined {
  const rule = API_ORIGIN_RULES.find(
    ({ host }) => hostname === host || hostname.endsWith(`.${host}`)
  );
  return rule?.apiOrigin;
}

/** `${origin}/api` for a known River host, falling back to local development. */
export function getApiBaseUrlForHostname(hostname: string): string {
  return `${getApiOriginForHostname(hostname) ?? LOCAL_API_ORIGIN}/api`;
}
